import { useCallback, useEffect, useState } from 'react';
import { fetchSkillAssets } from '../services/difyAssetsService';
import type { SkillOption } from '../types/skillAsset';

export interface UseSkillAssetsResult {
  /** 可选的技能列表 */
  readonly options: readonly SkillOption[];
  /** 加载中 */
  readonly loading: boolean;
  /** 当前选中的技能 ID 列表 */
  readonly selectedIds: readonly string[];
  /** 切换选中/取消 */
  readonly toggleSkill: (id: string) => void;
  /** 清空选中 */
  readonly clearSelection: () => void;
  /** 手动刷新 */
  readonly reload: () => void;
}

/**
 * 管理技能资产的获取和多选状态
 * @param capabilityId - 当前能力的数据库 id（ai_dify_capability.id）
 * @param enabled - 技能功能是否启用，false 时不请求
 */
export const useSkillAssets = (
  capabilityId: number | undefined,
  enabled: boolean
): UseSkillAssetsResult => {
  const [options, setOptions] = useState<readonly SkillOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [reloadFlag, setReloadFlag] = useState<number>(0);

  // capabilityId 或 enabled 变化时清空选中
  useEffect(() => {
    setSelectedIds([]);
  }, [capabilityId, enabled]);

  // 加载数据
  useEffect(() => {
    if (!enabled || !capabilityId) {
      setOptions([]);
      return;
    }

    let mounted = true;
    setLoading(true);

    fetchSkillAssets(capabilityId)
      .then((result: readonly SkillOption[]) => {
        if (mounted) {
          setOptions(result);
        }
      })
      .catch((err: unknown) => {
        console.error('[useSkillAssets] 加载失败:', err);
        if (mounted) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [capabilityId, enabled, reloadFlag]);

  const toggleSkill = useCallback((id: string): void => {
    setSelectedIds((prev: readonly string[]) => {
      const exists = prev.includes(id);
      return exists
        ? prev.filter((item: string): boolean => item !== id)
        : [...prev, id];
    });
  }, []);

  const clearSelection = useCallback((): void => {
    setSelectedIds([]);
  }, []);

  const reload = useCallback((): void => {
    setReloadFlag((prev: number) => prev + 1);
  }, []);

  return { options, loading, selectedIds, toggleSkill, clearSelection, reload };
};